import { GetStaticPaths, GetStaticProps } from 'next'
import { ReactElement } from 'react'

import { dehydrate, QueryClient } from 'react-query'

import { getBlogArticleSlugs } from 'app/services/Api/blog'
import { blogConfig } from 'app/services/Query/blog/blog.query'
import { NextPage } from 'app/types/decalarations/next'
import { getRelevantArticlesQueryParams } from 'app/utils/helpers/blog.helpers'

import MainLayout from '~/components/layouts/MainLayout'
import ArticleView from '~/views/Blog/ArticleView'

const ArticlePage: NextPage = () => <ArticleView />

ArticlePage.getLayout = (page: ReactElement) => <MainLayout>{page}</MainLayout>

export const getStaticPaths: GetStaticPaths = async () => {
  const { slugs } = await getBlogArticleSlugs()

  const paths =
    slugs.map(({ slug, category_slug }) => ({
      params: { slug, category: category_slug }
    })) ?? []

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug as string
  const categorySlug = context.params?.category as string
  const relevantParams = getRelevantArticlesQueryParams({ slug })

  try {
    const queryClient = new QueryClient()

    const { getArticleBySlug, getArticles, getCategories } = blogConfig

    await queryClient.prefetchQuery(getCategories.key, getCategories.request)

    const categoriesState = queryClient.getQueryState<BlogCategoryModel[]>(
      getCategories.key
    )

    const categories = categoriesState?.data
    const currentCategory = categories?.find(
      (item) => item.slug === categorySlug
    )

    if (!currentCategory?.id) throw new Error('Category not found')

    const articleKey = getArticleBySlug.key(slug)
    const relevantArticlesKey = getArticles.key(relevantParams)

    await queryClient.prefetchQuery(articleKey, getArticleBySlug.request)
    await queryClient.prefetchQuery(relevantArticlesKey, getArticles.request)

    const articleState = queryClient.getQueryState(articleKey)

    if (articleState?.status === 'error') {
      throw new Error(String(articleState?.error))
    }

    return {
      props: {
        dehydratedState: dehydrate(queryClient)
      }
    }
  } catch (error) {
    return { notFound: true }
  }
}

export default ArticlePage
